import { useState, useEffect, useCallback } from 'react'
import { fetchSeatMap } from '@services/seatMap.service'
import type { SeatMapData } from '@components/SeatMap/SeatMap.types'

const POLL_INTERVAL_MS = 30_000

interface UseSeatMapResult {
  seatMap: SeatMapData | null
  loading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Fetches and periodically refreshes the seat map for a given event.
 * Polls every 30 seconds to pick up status changes from other users.
 */
export function useSeatMap(eventId: number | null): UseSeatMapResult {
  const [seatMap, setSeatMap] = useState<SeatMapData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (eventId === null) return
    setLoading(true)
    setError(null)
    try {
      const data = await fetchSeatMap(eventId)
      setSeatMap(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar el mapa')
    } finally {
      setLoading(false)
    }
  }, [eventId])

  // Initial load
  useEffect(() => {
    void load()
  }, [load])

  // Polling — only when the map is visible
  useEffect(() => {
    if (eventId === null) return
    const id = setInterval(() => void load(), POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [eventId, load])

  return { seatMap, loading, error, refetch: load }
}
